import {
  Box,
  FormikSelectInputNative,
  FormikTextInput,
  Button,
  SelectInput,
  SimulatedEventPayloadType,
} from '@palmetto/palmetto-components';
import { Field } from 'formik';
import { ChangeEvent, useCallback } from 'react';

import { resolveSelectedOption } from './AvlFormUtils';

type SelectOption = { label: string; value: string };

export type ModelSelectChangeEvent = ChangeEvent<HTMLSelectElement> | SimulatedEventPayloadType;

type SystemAssetProps = {
  formPath: string;
  index: number;
  modelLabel: (index: number) => string;
  modelOptions: SelectOption[];
  modelValue?: string;
  useSearchableModelSelect?: boolean;
  showVintage?: boolean;
  vintageLabel?: (index: number) => string;
  vintageOptions?: SelectOption[];
  hideQuantity?: boolean;
  isDisabled: boolean;
  onRemove?: (index: number) => void;
  allowRemoveFirst?: boolean;
  onQuantityChange: (fieldPath: string, e: ChangeEvent<HTMLInputElement>) => void;
  onModelChange?: (fieldPath: string, e: ModelSelectChangeEvent) => void;
};

export const SystemAssetModel = ({
  formPath,
  index,
  modelLabel,
  modelOptions,
  modelValue,
  useSearchableModelSelect = false,
  showVintage,
  vintageLabel,
  vintageOptions,
  hideQuantity,
  isDisabled,
  onRemove,
  allowRemoveFirst = false,
  onQuantityChange,
  onModelChange,
}: SystemAssetProps) => {
  const modelPath = `${formPath}.${index}.model`;
  const vintagePath = `${formPath}.${index}.vintage`;
  const quantityPath = `${formPath}.${index}.count`;

  const handleSearchableModelChange = useCallback(
    (event: SimulatedEventPayloadType) => onModelChange?.(modelPath, event),
    [modelPath, onModelChange],
  );

  return (
    <Box
      direction={{ base: 'column', tablet: 'row' }}
      margin={index > 0 ? 'lg 0 0 0' : '0'}
      childGap={{ base: 'lg', desktop: 'xl' }}
    >
      {useSearchableModelSelect ? (
        <SelectInput
          id={modelPath}
          name={modelPath}
          label={modelLabel(index)}
          options={modelOptions}
          value={resolveSelectedOption(modelOptions, modelValue)}
          onChange={handleSearchableModelChange}
          isClearable
          isDisabled={isDisabled}
        />
      ) : (
        <Field
          label={modelLabel(index)}
          name={modelPath}
          id={modelPath}
          component={FormikSelectInputNative}
          options={modelOptions}
          isDisabled={isDisabled}
          onChange={onModelChange ? (e: ChangeEvent<HTMLSelectElement>) => onModelChange(modelPath, e) : undefined}
        />
      )}

      {showVintage && (vintageOptions?.length ?? 0) > 1 && (
        <Field
          label={vintageLabel ? vintageLabel(index) : 'Vintage'}
          maxWidth="xl"
          name={vintagePath}
          id={vintagePath}
          component={FormikSelectInputNative}
          options={vintageOptions ?? [{ label: 'None', value: 'none' }]}
          isDisabled={isDisabled}
        />
      )}

      {!hideQuantity && (
        <Field
          label={'Quantity'}
          name={quantityPath}
          id={quantityPath}
          component={FormikTextInput}
          isDisabled={isDisabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onQuantityChange(quantityPath, e)}
          maxWidth="lg"
        />
      )}

      {(allowRemoveFirst || index > 0) && onRemove && (
        <Button
          variant="secondary"
          tone="danger"
          onClick={() => onRemove(index)}
          isDisabled={isDisabled}
          iconPrefix="remove"
          size="sm"
          style={{ margin: '25px 0 0 0' }}
        />
      )}
    </Box>
  );
};
